/**
 * 多言語対応ヘルパー関数
 *
 * LanguageMap構造のテキストを、指定された言語で取得するための
 * ユーティリティ関数を提供します。
 *
 * @see docs/system.md
 */

import type { LanguageMap } from '@/types/domain';

/**
 * サポートされている言語コード
 */
export type SupportedLanguage = 'ja' | 'en';

/**
 * LanguageMap型かどうかを判定する型ガード
 *
 * @param value - 判定する値
 * @returns LanguageMap型の場合true
 */
function isLanguageMap(
  value: string | LanguageMap | undefined
): value is LanguageMap {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value.ja !== undefined || value.en !== undefined)
  );
}

/**
 * 多言語テキストから指定言語のテキストを取得
 *
 * フォールバック順序:
 * 1. 指定言語のテキスト
 * 2. 日本語 (ja)
 * 3. 英語 (en)
 * 4. LanguageMapの最初の値
 * 5. 空文字列
 *
 * @param text - 多言語テキストまたは文字列
 * @param lang - 取得したい言語コード ('ja' | 'en')
 * @returns 指定言語のテキスト
 *
 * @example
 * ```typescript
 * // 文字列の場合はそのまま返す
 * getLocalizedText('タイトル', 'ja'); // => 'タイトル'
 *
 * // LanguageMapの場合は指定言語を返す
 * getLocalizedText({ja: 'タクンマイム', en: 'Takun Mime'}, 'en');
 * // => 'Takun Mime'
 *
 * // 指定言語がない場合は日本語にフォールバック
 * getLocalizedText({ja: 'タクンマイム'}, 'en');
 * // => 'タクンマイム'
 * ```
 */
export function getLocalizedText(
  text: string | LanguageMap | undefined,
  lang: SupportedLanguage
): string {
  // undefined/nullの場合は空文字列を返す
  if (text == null) {
    return '';
  }

  // 文字列の場合はそのまま返す
  if (typeof text === 'string') {
    return text;
  }

  // LanguageMap型の場合
  if (isLanguageMap(text)) {
    // 1. 指定言語が存在する場合
    if (text[lang]) {
      return text[lang];
    }

    // 2. 日本語にフォールバック
    if (text.ja) {
      return text.ja;
    }

    // 3. 英語にフォールバック
    if (text.en) {
      return text.en;
    }

    // 4. 最初の値にフォールバック
    const values = Object.values(text).filter(
      (v): v is string => typeof v === 'string'
    );
    if (values.length > 0 && values[0]) {
      return values[0];
    }
  }

  // 5. すべて失敗した場合は空文字列
  return '';
}

/**
 * 複数の多言語テキストから指定言語のテキストを一括取得
 *
 * @param texts - 多言語テキストの配列
 * @param lang - 取得したい言語コード
 * @returns 指定言語のテキスト配列
 *
 * @example
 * ```typescript
 * const titles = [
 *   {ja: 'タイトル1', en: 'Title 1'},
 *   {ja: 'タイトル2', en: 'Title 2'},
 * ];
 * getLocalizedTexts(titles, 'en');
 * // => ['Title 1', 'Title 2']
 * ```
 */
export function getLocalizedTexts(
  texts: (string | LanguageMap | undefined)[],
  lang: SupportedLanguage
): string[] {
  return texts.map((text) => getLocalizedText(text, lang));
}

/**
 * カタカナ読みを取得
 *
 * LanguageMapからja-Kanaフィールドを取得します。
 * ja-Kanaが存在しない場合はundefinedを返します。
 *
 * @param text - 多言語テキストまたは文字列
 * @returns カタカナ読み、存在しない場合はundefined
 *
 * @example
 * ```typescript
 * getKanaReading({ja: '赤とんぼ', en: 'DRAGON FLY', 'ja-Kana': 'アカトンボ'});
 * // => 'アカトンボ'
 *
 * getKanaReading({ja: 'ZURAW', en: 'ZURAW'});
 * // => undefined
 *
 * getKanaReading('タイトル');
 * // => undefined (文字列の場合)
 * ```
 */
export function getKanaReading(
  text: string | LanguageMap | undefined
): string | undefined {
  if (text === undefined || text === null) {
    return undefined;
  }

  // 文字列の場合はundefined
  if (typeof text === 'string') {
    return undefined;
  }

  // LanguageMap型の場合
  if (isLanguageMap(text)) {
    return text['ja-Kana'];
  }

  return undefined;
}

/**
 * 言語コードが有効かどうかを判定
 *
 * @param lang - 検証する言語コード
 * @returns 有効な言語コードの場合true
 *
 * @example
 * ```typescript
 * isValidLanguage('ja'); // => true
 * isValidLanguage('en'); // => true
 * isValidLanguage('fr'); // => false
 * ```
 */
export function isValidLanguage(lang: string): lang is SupportedLanguage {
  return lang === 'ja' || lang === 'en';
}

/**
 * LanguageMapに指定言語のテキストが存在するかを判定
 *
 * @param text - 多言語テキストまたは文字列
 * @param lang - 確認する言語コード
 * @returns 指定言語のテキストが存在する場合true
 *
 * @example
 * ```typescript
 * hasLanguage({ja: 'テキスト', en: 'Text'}, 'en'); // => true
 * hasLanguage({ja: 'テキスト'}, 'en'); // => false
 * ```
 */
export function hasLanguage(
  text: string | LanguageMap | undefined,
  lang: SupportedLanguage
): boolean {
  if (typeof text === 'string' || text === undefined) {
    return false;
  }
  // LanguageMap型の場合のみtrue/falseを返す
  if (isLanguageMap(text)) {
    return Boolean(text[lang]);
  }
  return false;
}
